const API_BASE = 'https://api.syphrax.com';

let authToken: string | null = null;
let currentDeviceId: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function setDeviceId(id: string | null): void {
  currentDeviceId = id;
}

export function getDeviceId(): string | null {
  return currentDeviceId;
}

// Permite que App.tsx registre un callback para reaccionar a 401 globalmente
// (limpiar IndexedDB + localStorage + redirigir a Auth) sin que el usuario haga nada.
export function setOnUnauthorized(cb: (() => void) | null): void {
  onUnauthorized = cb;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(init.headers as Record<string, string> ?? {}) };
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  if (currentDeviceId) headers['X-Device-Id'] = currentDeviceId;
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (res.status === 401) {
    // Token caducado, cuenta borrada o secret cambiado: limpiar y forzar Auth.
    // No diferenciamos motivos — desde el cliente la única respuesta válida es relogin.
    if (authToken && onUnauthorized) {
      authToken = null;
      try { onUnauthorized(); } catch (_e) { /* ignore */ }
    }
    throw new Error('HTTP 401: unauthorized');
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export interface RegisterPayload {
  username: string;
  password: string;
  email?: string;
  public_key: string;
  private_key_encrypted: string;
  private_key_salt: string;
}

export interface AuthResponse {
  account_id: string;
  syphrax_id: string;
  token: string;
  public_key?: string;
  private_key_encrypted?: string;
  private_key_salt?: string;
  signing_private_encrypted?: string | null;
  prekey_material_encrypted?: string | null;
  ratchet_sessions_encrypted?: string | null;
}

export interface SendMessagePayload {
  receiver_tag: string;
  sender_tag: string;
  freq_tag: string | null;
  nonce: string;
  ciphertext: string;
  sender_envelope: string | null;
  type?: 'msg' | 'media' | 'ctrl';
  // Hint para el server sobre qué tipo de push notification disparar.
  // Si ausente: msg/media → 'message', ctrl → 'silent'. Solo el cliente sabe si un ctrl
  // es signaling de llamada (y debe sonar) o ruido como read receipts/self-copies (silencioso).
  push_kind?: 'message' | 'call' | 'silent';
  // Multi-device: device target específico del receptor. Si null, fan-out a todos
  // los devices del receiver_tag (legacy/broadcast).
  target_device_id?: string | null;
  // Capa de conversación lógica (v016): ULID estable que agrupa fan-out + self-copy.
  message_id?: string;
  // Legacy (rediseño 2026-05-03): el server lo IGNORA. El state ratchet vive
  // sólo en el dispositivo. Mantenido en el tipo por compat con código viejo.
  ratchet_session_blob?: string | null;
}

export interface IncomingBlob {
  id: string;
  sender_tag: string;
  freq_tag: string | null;
  nonce: string;
  ciphertext: string;
  sender_envelope: string | null;
  type: string;
  target_device_id?: string | null;
  message_id?: string | null;
  created_at: string;
  expires_at: string;
}

export interface DeviceInfo {
  id: string;
  name: string | null;
  platform: string | null;
  user_agent?: string | null;
  identity_x_public: string;
  identity_sign_public: string;
  last_seen: string | null;
}

export interface MyDeviceInfo extends DeviceInfo {
  identity_x_private_enc: string | null;
  identity_sign_private_enc: string | null;
  identity_salt: string | null;
  prekey_material_enc: string | null;
  created_at: string;
}

export interface DevicePrekeyBundle {
  device_id: string;
  syphrax_id: string;
  identity_x_public: string;
  identity_sign_public: string;
  spk_id: number;
  spk_public: string;
  spk_signature: string;
  spk_pq_kem_public: string | null;
  opk_id: number | null;
  opk_public: string | null;
  opk_pq_kem_public: string | null;
}

export const api = {
  register: (p: RegisterPayload) => request<AuthResponse>('/auth/register', { method: 'POST', body: JSON.stringify(p) }),
  login: (username: string, password: string) =>
    request<AuthResponse>('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  changePassword: (p: { current_password: string; new_password: string; private_key_encrypted: string; private_key_salt: string }) =>
    request<{ ok: boolean }>('/auth/change-password', { method: 'POST', body: JSON.stringify(p) }),
  updateAccount: (p: { email_optional?: string | null; presence_visibility?: 'all' | 'contacts' | 'none' }) =>
    request<{ ok: boolean }>('/account', { method: 'PATCH', body: JSON.stringify(p) }),
  heartbeat: (p: { visibility?: 'all' | 'contacts' | 'none'; freq_tag?: string | null }) =>
    request<{ ok: boolean; expires_at: string }>('/account/heartbeat', { method: 'POST', body: JSON.stringify(p) }),
  goOffline: () => request<{ ok: boolean }>('/account/offline', { method: 'POST', body: JSON.stringify({}) }),
  iceServers: () => request<{ iceServers: RTCIceServer[]; ttl_seconds: number; expires_at: string }>('/webrtc/credentials'),
  getPreferences: () => request<{ blob: string | null; updated_at: string | null }>('/account/preferences'),
  putPreferences: (blob: string) =>
    request<{ ok: boolean }>('/account/preferences', { method: 'PUT', body: JSON.stringify({ blob }) }),
  getUser: (syphraxId: string, freqTag?: string | null) => {
    const path = `/users/${encodeURIComponent(syphraxId)}` +
      (freqTag !== undefined ? `?freq_tag=${freqTag === null ? '' : encodeURIComponent(freqTag)}` : '');
    return request<{
      syphrax_id: string;
      public_key: string;
      presence?: {
        visibility: 'all' | 'contacts' | 'none';
        online: boolean;
        online_freq_tag: string | null;
        asked_freq_tag: string | null;
        last_seen_in_freq: string | null;
      };
    }>(path);
  },
  sendMessage: (payload: SendMessagePayload) => {
    // Si el payload viene del engine (ya enviado), respondemos directamente
    // sin volver a hacer POST. Esto permite que el código viejo de Chat.tsx
    // siga llamando api.sendMessage(blob) sin doble envío.
    const p = payload as any;
    if (p.__engine_sent) {
      return Promise.resolve({
        id: p.__engine_id as string,
        created_at: p.__engine_created_at as string,
        expires_at: p.__engine_created_at as string,
      });
    }
    return request<{ id: string; message_id: string | null; created_at: string; expires_at: string }>('/messages', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  fetchMessages: (params: { receiver_tag: string; freq_tag: string | null; since?: string }) => {
    // receiver_tag and freq_tag travel as headers (kept out of URL/logs).
    const q = new URLSearchParams();
    if (params.since) q.set('since', params.since);
    const path = q.toString() ? `/messages?${q.toString()}` : '/messages';
    const headers: Record<string, string> = {
      'X-Receiver-Tag': params.receiver_tag,
      'X-Freq-Tag': params.freq_tag === null ? '' : params.freq_tag,
    };
    return request<{ messages: IncomingBlob[] }>(path, { headers });
  },
  uploadMedia: async (encryptedBytes: Uint8Array): Promise<{ id: string; expires_at: string }> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/octet-stream',
      'X-Syphrax-Size': String(encryptedBytes.byteLength),
    };
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    const res = await fetch(`${API_BASE}/media`, { method: 'POST', headers, body: encryptedBytes as BodyInit });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
    return res.json();
  },
  downloadMedia: async (mediaId: string): Promise<Uint8Array> => {
    const headers: Record<string, string> = {};
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    const res = await fetch(`${API_BASE}/media/${encodeURIComponent(mediaId)}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
    return new Uint8Array(await res.arrayBuffer());
  },
  // Modelo simple (2026-05-04): sin prekeys, sin per-device identities. Los endpoints
  // /prekeys/* y /devices/* están eliminados. Para enviar a un peer basta /users/:id
  // (devuelve public_key); el cifrado es sealed_box puro.

  // Shims no-op por compat — devuelven valores neutros para que llamadas residuales
  // del código viejo en Chat.tsx no rompan en runtime.
  uploadPrekeys: async (_b: any): Promise<{ ok: boolean; opks_uploaded: number }> => ({ ok: true, opks_uploaded: 0 }),
  prekeyStatus: async (): Promise<{ opks_remaining: number }> => ({ opks_remaining: 1000 }),
  getUserPrekey: async (_id: string): Promise<any> => { throw new Error('prekeys removed in simple model'); },
  registerDevice: async (_p: any): Promise<{ device_id: string; created_at: string }> => ({ device_id: '', created_at: new Date().toISOString() }),
  listMyDevices: async (): Promise<{ devices: MyDeviceInfo[] }> => ({ devices: [] }),
  listPeerDevices: async (_id: string): Promise<{ devices: DeviceInfo[] }> => ({ devices: [] }),
  getDevicePrekey: async (_id: string): Promise<DevicePrekeyBundle> => { throw new Error('devices removed in simple model'); },
  uploadDeviceOpks: async (_id: string, _opks: any): Promise<{ added: number }> => ({ added: 0 }),
  deleteDevice: async (_id: string): Promise<{ ok: boolean }> => ({ ok: true }),
  deviceHeartbeat: async (_id: string): Promise<{ ok: boolean }> => ({ ok: true }),

  // ============= Archive (v016) — historial cifrado replicado entre devices =============
  // CREATE-ONLY: si el chunk ya existe, server NO lo sobreescribe.
  putArchiveChunk: (p: { conversation_tag: string; message_id: string; ciphertext: string }) =>
    request<{ id: string | null; created_at: string | null; created: boolean }>('/archive/chunks', {
      method: 'PUT', body: JSON.stringify(p),
    }),
  // UPDATE de flags: re-escribe ciphertext de un chunk YA EXISTENTE. 404 si no existe.
  putArchiveChunkFlags: (p: { conversation_tag: string; message_id: string; ciphertext: string }) =>
    request<{ id: string; created_at: string; updated: true }>('/archive/chunks/flags', {
      method: 'PUT', body: JSON.stringify(p),
    }),
  listArchiveConversations: () =>
    request<{ conversations: Array<{ conversation_tag: string; count: number; last_created_at: string; first_created_at: string }> }>('/archive/conversations'),
  getArchiveChunks: (p: { conversation_tag: string; before?: string; limit?: number }) => {
    const q = new URLSearchParams();
    q.set('conversation_tag', p.conversation_tag);
    if (p.before) q.set('before', p.before);
    if (p.limit) q.set('limit', String(p.limit));
    return request<{ chunks: Array<{ id: string; message_id: string; ciphertext: string; created_at: string }> }>('/archive/chunks?' + q.toString());
  },
  deleteArchiveChunk: (messageId: string) =>
    request<{}>('/archive/chunks/' + encodeURIComponent(messageId), { method: 'DELETE' }),

  // ============= Blocks (v016) — server-enforced silent reject =============
  listBlocks: () =>
    request<{ blocks: Array<{ blocked_username: string; created_at: string }> }>('/blocks'),
  block: (username: string) =>
    request<{ blocked: boolean; reason?: string }>('/blocks', {
      method: 'POST', body: JSON.stringify({ username }),
    }),
  unblock: (username: string) =>
    request<{}>('/blocks/' + encodeURIComponent(username), { method: 'DELETE' }),

  // ============= Privacy (v016) — server-stored privacy_settings =============
  getPrivacy: () =>
    request<{ privacy: { read_receipts: boolean; last_seen: 'everyone' | 'contacts' | 'nobody'; online_visibility: 'everyone' | 'contacts' | 'nobody' } }>('/privacy'),
  putPrivacy: (p: { read_receipts?: boolean; last_seen?: 'everyone' | 'contacts' | 'nobody'; online_visibility?: 'everyone' | 'contacts' | 'nobody' }) =>
    request<{ privacy: { read_receipts: boolean; last_seen: string; online_visibility: string } }>('/privacy', {
      method: 'PUT', body: JSON.stringify(p),
    }),
};

export { API_BASE };
